import countries, {Country} from "world-countries";
import {getCountryName, getCountryNameByCode, i18n, Locale} from "~/i18n-config";
import type {UrlObject} from "url";
import {SearchParams} from "~/utils/params";

export const countryRegions = ["Europe", "United States", "Canada"];
export const countrySubregions = [
    "Northern Europe",
    "Western Europe",
    "Central Europe",
    "Southern Europe",
    "Eastern Europe"
];

export const supportedCountryCodes = [
    "DK", "SE", "NO", "FI", "PL", "DE", "AT", "CH", "FR", "GB", "IE", "ES", "PT", "IT", "CZ", "SK", "HR", "US"
];

export const languageNamesByLocale: Record<Locale, string> = {
    de: "Deutsch",
    en: "English",
    fr: "Français",
    es: "Español",
    pt: "Português",
} as const;

export const getLanguageNameByLocale = (code: Locale) => languageNamesByLocale[code] ?? code;

/**
 * Get country by alias
 *
 * @param alias
 */
export const getCountry = (alias: string): Country => {
    const normalizedAlias = alias.trim().toUpperCase();
    return countries.find(country => country.cca2 === normalizedAlias) as Country;
};

/**
 * Get flag for country
 *
 * @param countryCode
 */
export function getFlagForCountry(countryCode: string): string {
    if (!/^[a-z]{2}$/i.test(countryCode)) return "";
    const codePoints = [...countryCode.toUpperCase()]
        .map(char => 0x1F1E6 + char.charCodeAt(0) - 65);
    return String.fromCodePoint(...codePoints);
}

/**
 * Translate the country name
 *
 * Uses the cached + fallback logic from `getCountryName`.
 *
 * @param lang - Locale (BCP-47, e.g. "en", "pt-BR").
 * @param country - Country object (from `world-countries`).
 * @returns Localized country name, or "Country value missing" if input is invalid.
 */
export const transCountryName = (lang: Locale, country: Country): string => {
    if (!country) return "Country value missing";
    return getCountryName(lang, country);
};

/**
 * Translate the country name by alias
 *
 * Looks up a `Country` by its alias, then delegates to `getCountryNameByCode`.
 *
 * @param lang - Locale (BCP-47, e.g. "en", "pt-BR").
 * @param alias - Country alias or ISO code.
 * @returns Localized country name, or "Country value missing" if not found.
 */
export const transCountryNameByAlias = (
    lang: Locale,
    alias: string
): string => {
    if (!alias) return "";
    const country = getCountry(alias);
    if (!country) return alias;
    return getCountryNameByCode(lang, country.cca2);
};

/**
 * Translate entity value
 *
 * @param lang the language to translate
 * @param name the name of the property to translate
 * @param entity the entity containing the language properties
 * @returns string
 */
export const transEntityValue = (lang: string, name: string, entity: any): string => {
    if (!entity) return "";

    if (entity[name]) {
        return String(entity[name]);
    }

    const translations = Array.isArray(entity.translations)
        ? entity.translations
        : [];
    const locale = lang.toLowerCase();
    const language = locale.split("-")[0];
    const findTranslation = (candidate: string) => translations.find(
        (translation: {locale?: unknown}) =>
            typeof translation.locale === "string"
            && translation.locale.toLowerCase() === candidate,
    );
    const requested = findTranslation(locale) || findTranslation(language);
    const fallback = findTranslation(i18n.defaultLocale);
    const value = (translation: Record<string, unknown> | undefined) =>
        translation?.[name]
        || (name === "label" ? translation?.name : undefined);

    return String(value(requested) || value(fallback) || "");
};

/**
 * Get default country region
 */
export const getDefaultCountry = () => {
    if(typeof navigator !== "undefined") {
        const languages = navigator.languages;
        if (languages && languages[0]) {
            const intl = new Intl.Locale(languages[0]);
            if (intl.region) {
                return intl.region.toUpperCase();
            }
        }
    }
    return "DE";
}

/**
 * Generate canonical + hreflang URLs for static pages based on their base path.
 *
 * @param lang the current page locale
 * @param pathname the page path of the current page (e.g., "/", "/signup", etc.)
 */
export const generateLocalizedMetadataAlternates = (
    lang: Locale | string,
    localizedPathnames: Partial<Record<Locale, string>>,
) => {
    const languages = Object.fromEntries(
        i18n.locales.flatMap((locale) => {
            const href = localizedPathnames[locale];
            return href ? [[locale, href]] : [];
        }),
    );
    const defaultHref = localizedPathnames[i18n.defaultLocale];
    const canonical = localizedPathnames[lang as Locale]
        ?? defaultHref
        ?? Object.values(languages)[0];

    if (!canonical) {
        throw new Error("At least one localized pathname is required for metadata alternates.");
    }

    return {
        canonical,
        languages: {
            ...languages,
            ...(defaultHref ? { "x-default": defaultHref } : {}),
        },
    };
};

export const generateMetadataAlternates = (lang: Locale | string, pathname: string) => {
    const localizedPathnames = Object.fromEntries(
        i18n.locales.map((locale) => [
            locale,
            locale === i18n.defaultLocale
                ? pathname
                : `/${locale}${pathname !== "/" ? pathname : ""}`,
        ]),
    ) as Record<Locale, string>;

    return generateLocalizedMetadataAlternates(lang, localizedPathnames);
};

/**
 * Get a localized URL string based on the given language and URL.
 *
 * @param lang - The current language code.
 * @param url - The original URL as a string or a `UrlObject` with a `pathname`.
 * @param searchParams - Optional search parameters as a record of key-value pairs.
 * @returns A localized URL string.
 */
export const localizePathname = (lang: string, url: string | UrlObject, searchParams?: URLSearchParams | SearchParams): string => {

    const isValidLocale = lang !== i18n.defaultLocale && i18n.locales.includes(lang as Locale);

    /**
     * Prepend the language to the given pathname if it's a valid locale.
     */
    const getLocalizedPathname = (pathname: string): string => {
        const parts = pathname.split("/").filter(Boolean);
        if (isValidLocale) parts.unshift(lang);
        return `/${parts.join("/")}`;
    };

    const pathname: string = (typeof url === "object" && url.pathname) ? String(url.pathname) : String(url);

    const hashtag = pathname.match(/#([^#]*)$/);
    const existingHashtag = hashtag ? `#${hashtag[1]}` : "";

    const pathnameWithoutHashtag = pathname.replace(/#([^#]*)$/, "");

    const [basePathname = "/", existingQueryString] = pathnameWithoutHashtag.split("?");

    /**
     * Handle search params
     */
    let urlSearchParams = new URLSearchParams();
    if (searchParams && (searchParams instanceof URLSearchParams || typeof searchParams === "object")) {
        urlSearchParams = new URLSearchParams(searchParams);
    }

    if (existingQueryString) {
        const existingQueryStringParams = new URLSearchParams(existingQueryString);
        for (const [key, value] of existingQueryStringParams) {
            urlSearchParams.set(key, value);
        }
    }

    if (typeof url === "object" && url.query) {
        const existingQueryParams = new URLSearchParams(url.query as any);
        for (const [key, value] of existingQueryParams) {
            urlSearchParams.set(key, value);
        }
    }

    const urlQueryString = (urlSearchParams.size > 0) ? `?${urlSearchParams.toString()}` : "";
    const localizedPathname = getLocalizedPathname(basePathname);

    return `${localizedPathname}${urlQueryString}${existingHashtag}`;

};

/**
 * Remove locale from pathname
 *
 * @param url the URL where the locale should be removed if existing
 * @returns a not localized URL
 */
export const removeLocaleFromPathname = (url: string | UrlObject): string => {

    let pathname = (typeof url === "object" && url.pathname) ? String(url.pathname) : String(url);

    for (const locale of i18n.locales) {

        const prefix = `/${locale}`;

        if (pathname.startsWith(`/${locale}`)) {
            const nextChar = pathname.charAt(prefix.length);

            if (nextChar === "" || nextChar === "/" || nextChar === "?") {
                pathname = pathname.slice(3) || "/";
                break;
            }
        }
    }

    return pathname;

}
